import { useMemo, useState } from 'react'
import { KNOWN_ANNOTATION_SEVERITIES } from './annotation-labels'
import {
  DEFAULT_ANNOTATION_PROXIMITY_RADIUS_M,
  filterAnnotationsByDateRange,
  filterAnnotationsBySeverity,
  filterAnnotationsNearRoute,
  filterPlottableAnnotations,
} from './annotation-utils'
import {
  type AnnotationGeoBox,
  filterAnnotationsWithinBox,
} from './annotation-viewport'
import type { AnnotationDateRange, TripAnnotationMarker } from './types'

export type UseAnnotationsStateOptions = {
  /**
   * Every annotation fetched for this map, unfiltered. Coordinates are validated
   * here (see `filterPlottableAnnotations`), so callers can hand over raw rows.
   */
  annotations: TripAnnotationMarker[]
  /**
   * Route polyline in `[lng, lat]` order that the "near this route" filter
   * measures against. Pass the polyline the map is already drawing; leave it out
   * on a map with no route and the proximity filter stays inert (it has nothing
   * to measure against) rather than blanking the layer.
   */
  route?: [number, number][]
  /**
   * Keep only the notes inside this box, normally the current metro's area
   * (see `annotationMetroBox`). Applied before every other filter and before
   * the count, so the menu never counts a note the map won't draw. Leave it
   * out on a map whose notes are already scoped (one trip).
   */
  within?: AnnotationGeoBox | null
  /** Older pages are still arriving. The Annotations menu says so. */
  loadingMore?: boolean
  /** Not every note was loaded. The Annotations menu says so. */
  truncated?: boolean
}

/**
 * Everything the annotation layer and its dropdown need, in one hook.
 *
 * Owning this here — rather than in a route-builder context — is what lets a
 * second map (trip-history) mount the same feature without inheriting
 * route-builder's provider: a caller creates one of these, hands it to
 * {@link AnnotationsButton} for the controls and to `useAnnotationMapLayer` for
 * the markers/heatmap, and the two stay in agreement because they read the same
 * object. The returned value is a plain object of state and setters, so it can
 * also be put on a context by a caller that needs to reach it from a sibling
 * (which is exactly what route-builder does with its map rail).
 */
export type AnnotationsState = {
  /**
   * Every plottable annotation, unfiltered. Kept separate from
   * {@link visibleAnnotations} so the layer toggle can show a true total even
   * while the layer itself is filtered down or hidden entirely.
   */
  allAnnotations: TripAnnotationMarker[]
  /** The layer actually rendered — narrowed by show/severity/date range/proximity. */
  visibleAnnotations: TripAnnotationMarker[]
  /**
   * Count after the severity and date filters but *before* the proximity pass,
   * i.e. the "of N" in "12 of 40 notes near this route". Derived here so the
   * button doesn't have to recompose the same filter chain to get it.
   */
  nearRouteTotal: number
  /** True once there is a polyline for the proximity filter to measure against. */
  hasRoute: boolean
  showAnnotations: boolean
  setShowAnnotations: (show: boolean) => void
  /** Severities currently visible. A severity with no checkbox always shows — see filterAnnotationsBySeverity. */
  severityFilter: string[]
  setSeverityFilter: (severities: string[]) => void
  range: AnnotationDateRange
  setRange: (range: AnnotationDateRange) => void
  /**
   * Density heatmap over the driver-notes layer. Reads the same filtered
   * annotations the markers do, so every layer filter narrows both together.
   */
  showHeatmap: boolean
  setShowHeatmap: (show: boolean) => void
  /**
   * Narrow the layer to notes near the route. Inert until there is a route (the
   * proximity predicate has no polyline to measure against), so with no route
   * the full fleet-wide set still shows.
   */
  nearRouteOnly: boolean
  setNearRouteOnly: (only: boolean) => void
  /** Proximity radius in metres used when {@link nearRouteOnly} is on. */
  nearRouteRadiusM: number
  setNearRouteRadiusM: (meters: number) => void
  /** Older pages are still arriving. */
  loadingMore: boolean
  /** Not every note was loaded (the page cap, or an error part-way). */
  truncated: boolean
}

export const useAnnotationsState = ({
  annotations,
  route,
  within = null,
  loadingMore = false,
  truncated = false,
}: UseAnnotationsStateOptions): AnnotationsState => {
  // Annotation layer controls default to "everything visible": on, every known
  // severity checked, no date range — so mounting the layer shows the same thing
  // the map showed before any of this UI existed.
  const [showAnnotations, setShowAnnotations] = useState(true)
  const [severityFilter, setSeverityFilter] = useState<string[]>(
    () => KNOWN_ANNOTATION_SEVERITIES,
  )
  const [range, setRange] = useState<AnnotationDateRange>()
  // Both the heatmap and the near-route filter default off: each layers extra
  // ink or hides notes over the road being looked at, so they are asked for
  // rather than on by default.
  const [showHeatmap, setShowHeatmap] = useState(false)
  const [nearRouteOnly, setNearRouteOnly] = useState(false)
  const [nearRouteRadiusM, setNearRouteRadiusM] = useState(
    DEFAULT_ANNOTATION_PROXIMITY_RADIUS_M,
  )

  // Keyed on the box's corners rather than its identity, so a caller that
  // builds the box inline doesn't re-filter the whole list every render.
  const withinKey = within
    ? `${within.west},${within.south},${within.east},${within.north}`
    : ''
  const allAnnotations = useMemo(() => {
    const plottable = filterPlottableAnnotations(annotations)
    if (!withinKey) return plottable
    const [west, south, east, north] = withinKey.split(',').map(Number)
    return filterAnnotationsWithinBox(plottable, { west, south, east, north })
  }, [annotations, withinKey])

  // The date + severity set, before proximity. Both the "of N" total and the
  // rendered layer derive from this one array, so the count on the dropdown can
  // never disagree with what the map is drawing.
  const beforeProximity = useMemo(
    () =>
      filterAnnotationsBySeverity(
        filterAnnotationsByDateRange(allAnnotations, range),
        severityFilter,
        KNOWN_ANNOTATION_SEVERITIES,
      ),
    [allAnnotations, range, severityFilter],
  )

  // Memoised rather than `route ?? []` inline: a fresh empty array every render
  // would invalidate the layer memo below on every render of a map that has no
  // route — i.e. exactly the caller for which the proximity filter does nothing
  // anyway would be the one paying to re-filter the whole layer.
  const routeCoordinates = useMemo(() => route ?? [], [route])

  const visibleAnnotations = useMemo(() => {
    if (!showAnnotations) return []
    if (!nearRouteOnly) return beforeProximity
    return filterAnnotationsNearRoute(
      beforeProximity,
      routeCoordinates,
      nearRouteRadiusM,
    )
  }, [
    beforeProximity,
    nearRouteOnly,
    nearRouteRadiusM,
    routeCoordinates,
    showAnnotations,
  ])

  return {
    allAnnotations,
    visibleAnnotations,
    nearRouteTotal: beforeProximity.length,
    hasRoute: routeCoordinates.length > 0,
    showAnnotations,
    setShowAnnotations,
    severityFilter,
    setSeverityFilter,
    range,
    setRange,
    showHeatmap,
    setShowHeatmap,
    nearRouteOnly,
    setNearRouteOnly,
    nearRouteRadiusM,
    setNearRouteRadiusM,
    loadingMore,
    truncated,
  }
}
